import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isNative } from '@/lib/platform/platform';
import { getSite, siteOrigin } from '@/lib/site';
import { headTags } from '@/lib/seo/head';
import { classifyPath } from '@/lib/seo/routes';
import { aboutMeta, authTitles, guidesMeta, homeMeta, noindexMeta, notFoundMeta, privacyMeta, refundsMeta, type SeoContext, type SeoMeta, storeMeta, supportMeta, termsMeta } from '@/lib/seo/meta';

export const seoContext = (): SeoContext => ({ origin: siteOrigin(), site: getSite() });

/**
 * Puts a page's meta into the document: title, description, indexing rule,
 * canonical and hreflang links, Open Graph and Twitter tags, structured data.
 * In production the Cloudflare Worker has already put the same tags into the
 * HTML, so this keeps them right as people move around the app (and is what
 * runs on local development and preview addresses).
 */
export function applySeo(ctx: SeoContext, meta: SeoMeta, options: { title?: boolean } = {}) {
  const head = document.head;
  // What the page had before: the app's defaults (no data-seo) and the last page's tags.
  head.querySelectorAll('[data-seo], meta[name="description"], meta[name="robots"], meta[property^="og:"], meta[name^="twitter:"], link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  if (options.title !== false) document.title = meta.title;
  const holder = document.createElement('template');
  holder.innerHTML = headTags(ctx, meta).join('');
  head.append(...Array.from(holder.content.children));
}

/** Sets a page's meta once its data is in (a product, a provider). */
export function useSeo(meta: SeoMeta | null) {
  useEffect(() => {
    if (meta && !isNative()) applySeo(seoContext(), meta);
  }, [meta]);
}

/**
 * Meta for every page that doesn't depend on loaded data: the fixed public
 * pages are indexed with their own title and description; sign-in, search, the
 * app and unknown addresses are marked noindex. Products and providers set
 * their own with useSeo once they've loaded.
 */
export function RouteSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isNative()) return;
    const ctx = seoContext();
    const { kind } = classifyPath(pathname);
    switch (kind) {
      case 'home':
        return applySeo(ctx, homeMeta(ctx));
      case 'store':
        return applySeo(ctx, storeMeta(ctx));
      case 'privacy':
        return applySeo(ctx, privacyMeta(ctx));
      case 'support':
        return applySeo(ctx, supportMeta(ctx));
      case 'terms':
        return applySeo(ctx, termsMeta(ctx));
      case 'refunds':
        return applySeo(ctx, refundsMeta(ctx));
      case 'about':
        return applySeo(ctx, aboutMeta(ctx));
      case 'guides':
        return applySeo(ctx, guidesMeta(ctx));
      case 'auth':
        return applySeo(ctx, noindexMeta(authTitles[pathname.replace(/\/$/, '')] ?? 'Sign in'));
      case 'search':
        return applySeo(ctx, noindexMeta('Search results'));
      case 'private':
        // The page names itself; this only keeps it out of search.
        return applySeo(ctx, noindexMeta('TradePrep'), { title: false });
      case 'unknown':
        return applySeo(ctx, notFoundMeta());
      default:
        // product / provider / guide: the page sets its own once loaded.
    }
  }, [pathname]);
  return null;
}
